import { createAsyncThunk,createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import api from '../../api';

// All of these asyncThunks are intended to be used by Admins, not users
export const getAllReservations = createAsyncThunk(
  'reservation/getAllReservations',
  async (equipmentId, { rejectWithValue }) => {
    try {
      let url = '/reservations/';
      if (equipmentId && typeof equipmentId === 'string' && equipmentId.trim() !== '') {
        url = `/reservations?equipmentId=${equipmentId}`;
      }
      const res = await api.get(url);
      return res.data.data;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to fetch all reservations.';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteReservation = createAsyncThunk(
  'reservation/deleteReservation',
  async (resId, { rejectWithValue }) => {
    try {
      await api.delete(`/reservations/${resId}`);
      toast.success('Deleted Reservation!');
      return resId;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to delete that reservation.';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateReservation = createAsyncThunk(
  'reservation/updateReservation',
  async (reservation, { rejectWithValue }) => {
    const resId = reservation._id;
    try {
      const res = await api.put(`/reservations/${resId}`, reservation);
      // toast.success('Success!');
      return res.data;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to change reservation info.';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const processReturn = createAsyncThunk(
  'reservation/processReturn',
  async ({ reservationId, returnData }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/reservations/${reservationId}/return`, returnData);
      toast.success('Return processed successfully');
      return res.data.data.reservation;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Failed to process return';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const createReservation = createAsyncThunk(
  'reservation/createReservation',
  async (reservation, { rejectWithValue }) => {
    try {
      const res = await api.post('/reservations', reservation);
      toast.success('Created Reservation!');
      return res.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || error.errors.message;
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  reservations: [],
  reservation: null,
  loading: false,
  error: null,
};

const reservationSlice = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllReservations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllReservations.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations = action.payload.reservations;
      })
      .addCase(getAllReservations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createReservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createReservation.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations.push(action.payload);
      })
      .addCase(deleteReservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteReservation.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations = state.reservations.filter(
          (reservation) => reservation._id !== action.payload
        );
      })
      .addCase(updateReservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateReservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReservation.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations = state.reservations.map((reservation) =>
          reservation._id === action.payload._id ? action.payload : reservation
        );
      })
      .addCase(processReturn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processReturn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(processReturn.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations = state.reservations.map((reservation) =>
          reservation._id === action.payload._id ? action.payload : reservation
        );
      });
  },
});

export const { clearError } = reservationSlice.actions;
export default reservationSlice.reducer;
